/**
 * BillingThroughTable
 *
 * Dense table for "Billing Through" relationships: provider, billing entity, effective dates, payers impacted.
 * UnifyTable (no outer Paper — parent provides surface). PHI-safe; dependency warnings shown.
 */

import { Group, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import type { BillingThroughRelationship } from '../types';

const COLUMNS: UnifyTableColumn<BillingThroughRelationship>[] = [
  {
    key: 'provider',
    label: 'Provider',
    render: (r) => <Text size="sm">{r.providerName}</Text>,
    getValue: (r) => r.providerName,
  },
  {
    key: 'billingEntity',
    label: 'Billing entity',
    render: (r) => <Text size="sm">{r.billingEntityName}</Text>,
    getValue: (r) => r.billingEntityName,
  },
  {
    key: 'effective',
    label: 'Effective',
    render: (r) => (
      <Text size="sm">
        {r.effectiveStart}
        {r.effectiveEnd ? ` – ${r.effectiveEnd}` : ''}
      </Text>
    ),
    getValue: (r) => `${r.effectiveStart} ${r.effectiveEnd ?? ''}`,
  },
  {
    key: 'payers',
    label: 'Payers impacted',
    render: (r) => <Text size="sm">{r.payersImpacted.join(', ')}</Text>,
    getValue: (r) => r.payersImpacted.join(', '),
  },
  {
    key: 'warnings',
    label: 'Warnings',
    render: (r) =>
      r.warnings?.length ? (
        <Group gap={4} wrap="wrap">
          <IconAlertTriangle size={14} aria-hidden />
          <Text size="sm" c="yellow.7">
            {r.warnings.join('; ')}
          </Text>
        </Group>
      ) : (
        <Text size="xs" c="dimmed">
          —
        </Text>
      ),
    getValue: (r) => r.warnings?.join('; ') ?? '',
  },
];

interface BillingThroughTableProps {
  relationships: BillingThroughRelationship[];
}

export function BillingThroughTable({ relationships }: BillingThroughTableProps): JSX.Element {
  return (
    <UnifyTable
      withPaper={false}
      columns={COLUMNS}
      data={relationships}
      getRowKey={(r) => r.id}
    />
  );
}
